/**
 * PostProcessingPipeline — wraps THREE.PostProcessing (TSL-based).
 *
 * Supports 25 post-processing effects via Three.js TSL nodes:
 *   AO, SSGI, SSR, Denoise, God Rays, DOF, Motion Blur, Bloom, Anamorphic,
 *   After Image, Outline, Film, Dot Screen, RGB Shift, Chromatic Aberration,
 *   Sepia, Grayscale, LUT 3D, Sobel, Gaussian Blur, Box Blur, Hash Blur,
 *   Transition, FXAA, SMAA, TRAA.
 *
 * Works on both WebGPU and WebGL2 backends.
 * Provides named pass management with enable/disable controls.
 */

import * as THREE from 'three/webgpu';
import { pass, perspectiveDepthToViewZ, orthographicDepthToViewZ, uniform, float, mix, vec2, vec4, convertToTexture, texture3D, renderOutput, step, clamp } from 'three/tsl';
import { SSR_REPLACE, SSR_HIT_EPS } from './post/ssrComposite.js';
import { skyMask } from './depth/skyMask.js';
import { installMrtOverlayBlending, mrtSurfaceOutput } from './post/mrtOverlay.js';
import { createEASUNode, createRCASNode, type EASUNodeResult, type RCASNodeResult } from './upscale/fsr/FSRNode.js';
import { FSR_PRESETS, type FSRQuality } from './upscale/fsr/FSRConfig.js';
import { Logger } from '../core/Logger.js';
import { createGodRaysNode, type GodRaysConfig, type GodRaysNodeResult } from './gi/GodRaysPass.js';
import type { LensFlareNodeResult } from './lensflares/LensFlareNode.js';
import { createLensFlareConfig, type LensFlareConfig } from './lensflares/LensFlareConfig.js';
import {
  createContactShadowsNode,
  getPrimaryLightDirView,
  type ContactShadowsUniforms,
} from './shadows/ContactShadowsNode.js';
import type { ContactShadowsConfig } from './shadows/ContactShadowsConfig.js';
import {
  createVignetteNode, createLensDistortionNode, createColorGradeNode, createSharpenNode,
  type VignetteConfig, type VignetteUniforms,
  type LensDistortionConfig, type LensDistortionUniforms,
  type ColorGradeConfig, type ColorGradeUniforms,
  type SharpenConfig, type SharpenUniforms,
} from './post/ScreenEffects.js';
import {
  createAerialCompositeNode,
  type AerialCompositeConfig, type AerialCompositeUniforms,
} from './sky/lut/aerialComposite.js';
import {
  createVolumetricFogNode,
  type VolumetricFogPassConfig, type VolumetricFogUniforms,
} from './fog/VolumetricFogPass.js';
import {
  createCapsuleShadowsNode,
  type CapsuleShadowsPassConfig, type CapsuleShadowsUniforms,
} from './shadows/capsule/CapsuleShadowsNode.js';

// ── Existing Effect Configs ─────────────────────────────────────────────────

export interface BloomConfig {
  threshold?: number;
  strength?: number;
  radius?: number;
  /**
   * 'mip' (default): three's BloomNode Gaussian mip chain. 'kawase': the
   * dual-filter bloom (Bjørge 2015 — two down + two up passes, a fraction of
   * the texture reads; the low-tier bloom, shader wave stage 4).
   */
  mode?: 'mip' | 'kawase';
  /** Kawase down/up pass pairs (default 2). */
  passes?: number;
}

export interface AOConfig {
  intensity?: number;
  radius?: number;
  samples?: number;
  /** GTAO render-target scale (0.5 = half resolution; AO is blurred anyway). Default 1. */
  resolutionScale?: number;
}

export interface FXAAConfig {
  enabled?: boolean;
  /** AA quality 2|4|8|16|32 → log2(q) iterated passes (see the AA section). */
  quality?: number;
}

export interface SSRConfig {
  intensity?: number;
  maxDistance?: number;
  thickness?: number;
  resolutionScale?: number;
}

export interface DOFConfig {
  /** Focus distance in world units (default 10). */
  focusDistance?: number;
  /**
   * NOT a lens focal length: this is the FOCUS RANGE in world units — how far
   * an object may be from the focus plane before it is COMPLETELY out of
   * focus. three computes `CoC = smoothstep(0, focalLength, |viewZ| −
   * focusDistance)`, so a small value blurs everything and a large value blurs
   * only what is far from the plane. Read as metres, not millimetres: a "50 mm
   * lens" reading of 0.05 means "fully blurred 5 cm off the focus plane"
   * (2026-09-14). Default 40 m.
   */
  focalLength?: number;
  /** Unitless bokeh size (default 5). */
  bokehScale?: number;
  /**
   * 'hex' switches to the engine's custom hexagonal-aperture gather bokeh
   * (BokehDofNode: blue-noise stratified adaptive taps) instead of three's
   * stock dof() — the previously-unwired module (2026-08-17 audit).
   */
  bokeh?: 'standard' | 'hex';
}

export interface MotionBlurConfig {
  /** Number of reconstruction taps (default 16; 8–32). */
  samples?: number;
  /** Trail-length multiplier on the velocity buffer (default 1). */
  intensity?: number;
  /** Hard cap on the blur radius in pixels (default 48) — a camera flick never streaks the whole screen. */
  maxBlurPx?: number;
  /** Tile size (px) for the dominant-velocity dilation (default 20). */
  tileSize?: number;
  /** Depth-aware tap weighting (default true): foreground blur smears over the background, never the reverse. */
  depthAware?: boolean;
}

export interface TonemapConfig {
  exposure?: number;
  toneMapping?: THREE.ToneMapping;
  /**
   * Friendly operator name — takes precedence over `toneMapping` when set.
   * 'aces' = ACESFilmic, 'agx' = AgX, 'neutral' = Khronos Neutral,
   * 'reinhard' / 'cineon' / 'linear' / 'none' as named.
   */
  operator?: 'aces' | 'agx' | 'neutral' | 'reinhard' | 'cineon' | 'linear' | 'none';
}

/** Map a friendly tonemap operator name to the three.js constant. */
export function toneMappingForOperator(op: NonNullable<TonemapConfig['operator']>): THREE.ToneMapping {
  switch (op) {
    case 'aces': return THREE.ACESFilmicToneMapping;
    case 'agx': return THREE.AgXToneMapping;
    case 'neutral': return THREE.NeutralToneMapping;
    case 'reinhard': return THREE.ReinhardToneMapping;
    case 'cineon': return THREE.CineonToneMapping;
    case 'linear': return THREE.LinearToneMapping;
    case 'none': return THREE.NoToneMapping;
  }
}

// ── New Effect Configs ──────────────────────────────────────────────────────

export interface AfterImageConfig {
  /** Damping factor 0–1 (default 0.96). Higher = longer trails. */
  damp?: number;
}

export interface AnamorphicConfig {
  /** Brightness threshold (default 0.9). */
  threshold?: number;
  /** Streak scale (default 3). */
  scale?: number;
  /** Blur samples (default 32). */
  samples?: number;
}

export interface BoxBlurConfig {
  /** Enable box blur. */
  enabled?: boolean;
}

export interface ChromaticAberrationConfig {
  /** Aberration strength (default 1.0). */
  strength?: number;
  /** Scale factor for stepped scaling from center (default 1.1). */
  scale?: number;
}

export interface DenoiseConfig {
  /** Enable denoising. Uses depth + normals from scene pass. */
  enabled?: boolean;
}

export interface DotScreenConfig {
  /** Pattern angle in radians (default pi/2 ≈ 1.57). */
  angle?: number;
  /** Pattern scale (default 1). */
  scale?: number;
}

export interface FilmConfig {
  /** Film grain intensity (default 1). */
  intensity?: number;
}

export interface GaussianBlurConfig {
  /** Blur kernel size (default 4). */
  sigma?: number;
  /** Resolution scale 0–1 (default 1). */
  resolutionScale?: number;
}

export interface GrayscaleConfig {
  /** Enable grayscale desaturation. */
  enabled?: boolean;
}

export interface HashBlurConfig {
  /** Blur amount (default 0.1). */
  amount?: number;
}

export interface Lut3DConfig {
  /** 3D LUT texture (required). */
  lut: THREE.Data3DTexture;
  /** LUT grid size (e.g. 8, 16, 32). */
  size: number;
  /** LUT intensity 0–1 (default 1). */
  intensity?: number;
}

export interface OutlineConfig {
  /** Objects to outline (required). */
  selectedObjects: THREE.Object3D[];
}

export interface RGBShiftConfig {
  /** Shift amount (default 0.005). */
  amount?: number;
  /** Shift angle in radians (default 0). */
  angle?: number;
}

export interface SepiaConfig {
  /** Enable sepia color grading. */
  enabled?: boolean;
}

export interface SMAAConfig {
  /** Enable SMAA anti-aliasing (mutually exclusive with FXAA/TRAA). */
  enabled?: boolean;
  /** AA quality 2|4|8|16|32 → log2(q) iterated passes (see the AA section). */
  quality?: number;
}

export interface SobelConfig {
  /** Enable Sobel edge detection. */
  enabled?: boolean;
}

export interface SSGIConfig {
  /** Enable screen-space global illumination. */
  enabled?: boolean;
  /** Slices per pixel (1–4; three's temporal presets: low 1, med 2, high 3). */
  sliceCount?: number;
  /** Steps per slice (1–32; temporal presets: 12 / 8 / 16). */
  stepCount?: number;
  /** Temporal filtering (default true; false needs a denoise pass). */
  temporal?: boolean;
  /** Indirect-light strength multiplier (default 1). */
  intensity?: number;
  /** How much of SSGI's own ambient-occlusion term darkens the scene (0..1, default 0.5). */
  aoStrength?: number;
}

export interface PostTransitionConfig {
  /** Second scene to transition to (required). */
  sceneB: THREE.Scene;
  /** Camera for second scene (required). */
  cameraB: THREE.Camera;
  /** Transition pattern texture. */
  mixTexture?: THREE.Texture;
  /** Blend ratio 0–1. 0 = scene A, 1 = scene B (default 0). */
  mixRatio?: number;
  /** Interpolation threshold (default 0.1). */
  threshold?: number;
  /** Whether mix texture influences transition (default false). */
  useTexture?: boolean;
}

export interface TRAAConfig {
  /** Enable Temporal Resolve Anti-Aliasing (mutually exclusive with FXAA/SMAA). */
  enabled?: boolean;
  /** AA quality 2|4|8|16|32 — ≥8 chains one FXAA behind the temporal resolve
   *  (TRAA itself must run exactly once; see the AA section). */
  quality?: number;
}

export interface ContactShadowsPassConfig extends Partial<ContactShadowsConfig> {
  /**
   * Set to false to disable the pass entirely (default: true when key is present).
   * The pass is only included in the pipeline when this key exists in PostProcessingConfig.
   */
  enabled?: boolean;
}

export interface CloudsPassConfig {
  /**
   * The cloud RGBA node — `.rgb` = in-scattered radiance, `.a` = opacity
   * (`1 - transmittance`). Produced by `CloudRenderer.buildCompositeNode()`.
   *
   * Compositing as a DEPTH-AWARE post pass (rather than in
   * `scene.backgroundNode`) is what makes the clouds world-anchored: the
   * cloud node ray-marches an altitude slab from the REAL camera position
   * (`ro = cameraPosition`, `rd` reconstructed from `screenUV` + the camera
   * matrices), so the clouds parallax + pan with the world, and the sky-pixel
   * depth mask lets terrain/buildings occlude them. In the background skybox
   * (a unit sphere drawn camera-locked at the far plane via a w=0 model-view
   * transform) the same finite-altitude clouds read as pinned to the view.
   */
  node: { rgb: unknown; a: unknown };
  /** Raymarch at this fraction of the drawing buffer (0.5 = quarter of the rays); default 1 = full. */
  resolutionScale?: number;
  /** Set false to skip the pass even when a node is supplied. Default true. */
  enabled?: boolean;
  /**
   * Composite AFTER the anti-aliasing stage instead of slot 0. Use with
   * TRAA: the temporal resolve's per-frame camera jitter makes the clouds'
   * binary depth mask flicker at geometry silhouettes and its blue-noise
   * ray jitter defeats the history clamp — the resolve smears both into
   * blocky dither. Post-AA clouds skip bloom (acceptable: they are lit
   * scattering, not emitters) but stay crisp and unsmeared.
   */
  afterAA?: boolean;
}

// ── Pipeline Config ─────────────────────────────────────────────────────────

/**
 * Spatial upscaling (FSR 1: EASU + RCAS) — the scene pass renders at
 * output / scaleFactor and the 12-tap edge-adaptive kernel reconstructs the
 * output resolution right after it, so every later pass sees a full-size
 * image. Cheaper than raw render scale at the same fragment count and much
 * sharper; the render scale can also be moved LIVE (`setRenderScale`) for a
 * frame-time governor without a rebuild.
 */
const _upscaleSize = new THREE.Vector2();

export interface UpscaleConfig {
  /** FSR quality preset (ultra 1.3×, quality 1.5×, balanced 1.7×, performance 2×) — sets the render ratio for both modes. */
  quality: FSRQuality;
  /** RCAS sharpness 0..1 (default: the preset's). */
  sharpness?: number;
  /**
   * 'fsr' (default): spatial — EASU right after the scene pass.
   * 'taau': TEMPORAL — the scene pass renders at 1/ratio and the temporal
   * resolve (three's TRAA node, auto-enabled, jittered, history-clamped)
   * accumulates the sub-pixel samples into an output-resolution history at
   * the AA slot; RCAS sharpens the resolve. Needs the velocity MRT (forced).
   */
  mode?: 'fsr' | 'taau';
}

export interface PostProcessingConfig {
  /** FSR 1 upscaling of the scene pass (see UpscaleConfig). */
  upscale?: UpscaleConfig;
  // Existing
  bloom?: BloomConfig;
  ao?: AOConfig;
  fxaa?: FXAAConfig;
  ssr?: SSRConfig;
  dof?: DOFConfig;
  motionBlur?: MotionBlurConfig;
  tonemap?: TonemapConfig;
  godRays?: Partial<GodRaysConfig> & { enabled?: boolean };
  /**
   * Screen-space (pseudo) lens flares — ghosts, starburst and streak from the
   * frame's own bright pixels (John Chapman 2013); no light positions needed.
   * Composited right after the god rays, before bloom/tonemap so it reads HDR.
   */
  lensFlares?: Partial<LensFlareConfig> & { enabled?: boolean };
  // New
  afterImage?: AfterImageConfig;
  anamorphic?: AnamorphicConfig;
  boxBlur?: BoxBlurConfig;
  chromaticAberration?: ChromaticAberrationConfig;
  denoise?: DenoiseConfig;
  dotScreen?: DotScreenConfig;
  film?: FilmConfig;
  gaussianBlur?: GaussianBlurConfig;
  grayscale?: GrayscaleConfig;
  hashBlur?: HashBlurConfig;
  lut3D?: Lut3DConfig;
  outline?: OutlineConfig;
  rgbShift?: RGBShiftConfig;
  sepia?: SepiaConfig;
  smaa?: SMAAConfig;
  sobel?: SobelConfig;
  ssgi?: SSGIConfig;
  transition?: PostTransitionConfig;
  traa?: TRAAConfig;
  /** Screen-space contact shadows — cheap high-frequency shadows near surfaces. */
  contactShadows?: ContactShadowsPassConfig;
  /** Analytic capsule shadows for character proxies (soft, map-free). */
  capsuleShadows?: CapsuleShadowsPassConfig;
  /**
   * Volumetric clouds composited as a depth-aware post pass. Composited EARLY
   * (right after the scene pass, before god rays / bloom / tone-map) so the
   * clouds receive bloom + tone-mapping like the rest of the HDR scene, and
   * only over sky pixels (depth ≈ far) so opaque geometry occludes them.
   */
  clouds?: CloudsPassConfig;
  /**
   * Accessibility colourblind correction. An optional final, full-screen
   * colour transform applied AFTER tone-mapping + anti-aliasing (i.e. on the
   * display-referred image). Supplied by the accessibility subsystem's
   * `AccessibilityModule.createColorblindTransform()`. When omitted the
   * pipeline is unchanged — fully backward-compatible.
   */
  colorblind?: ColorblindPassConfig;
  /**
   * Hillaire aerial-perspective composite — attenuates + haze-tints scene
   * geometry from the baked froxel volume (SkyLutSet.aerial). Applied right
   * after the clouds slot, on the raw HDR scene colour, so the haze flows
   * through bloom/tone-map like the sky it matches.
   */
  aerial?: AerialCompositeConfig;
  /** Lift/gamma/gain parametric colour grading (pre-LUT). */
  colorGrade?: ColorGradeConfig;
  /**
   * Analytic volumetric fog (closed-form height fog + sun in-scatter + local
   * box volumes) — applied right after aerial perspective, pre-AO/bloom.
   */
  volumetricFog?: VolumetricFogPassConfig;
  /** Radial vignette (post-AA, display-referred). */
  vignette?: VignetteConfig;
  /** Barrel/pincushion lens distortion (post-AA — it resamples the chain). */
  lensDistortion?: LensDistortionConfig;
  /** RCAS-style neighborhood-clamped sharpen (post-AA, pre-distortion). */
  sharpen?: SharpenConfig;
}

export interface ColorblindPassConfig {
  /**
   * Colour transform `(rgbNode) => rgbNode`. Applied as the very last step.
   * Set to false / omit to disable. The accessibility module rebuilds the
   * pipeline (or swaps this transform) when the colourblind mode changes.
   */
  transform?: (rgb: unknown) => unknown;
  /** Set false to skip even when a transform is present. Default true. */
  enabled?: boolean;
}

// ── Implementation ──────────────────────────────────────────────────────────

interface PassEntry {
  name: string;
  node: unknown;
  enabled: boolean;
}

export class PostProcessingPipeline {
  private renderer: THREE.WebGPURenderer;
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  // DOF near/far uniforms — refreshed in setCamera() and on per-frame render
  // when the camera's near/far have drifted from the cached values. Typed
  // as `any` because TSL `uniform()` returns a Node subclass that exposes
  // `.value`; we don't import the runtime type.
   
  private _dofNearUniform: any = null;
   
  private _dofFarUniform: any = null;
  private postProcessing: THREE.PostProcessing | null = null;
  private passes = new Map<string, PassEntry>();
  private _enabled = true;
  private _ready = false;
  private _disposed = false;
  private _godRays: GodRaysNodeResult | null = null;
  private _lensFlare: LensFlareNodeResult | null = null;
  /** Dual-Kawase bloom (stage 4) — resized with the drawing buffer. */
  private _kawase: { setSize(w: number, h: number): void } | null = null;
  private _contactShadowsUniforms: ContactShadowsUniforms | null = null;
  private _aerialUniforms: AerialCompositeUniforms | null = null;
  private _vignetteUniforms: VignetteUniforms | null = null;
  private _lensDistortionUniforms: LensDistortionUniforms | null = null;
  private _colorGradeUniforms: ColorGradeUniforms | null = null;
  private _sharpenUniforms: SharpenUniforms | null = null;
  private _volumetricFogUniforms: VolumetricFogUniforms | null = null;
  private _capsuleShadowsUniforms: CapsuleShadowsUniforms | null = null;
  private _scenePass: any = null; // PassNode — stored to update camera reference
  /** Fractional-resolution cloud RTT (null = full-res clouds or no clouds). */
  private _cloudRtt: { node: { setSize?: (w: number, h: number) => void }; rs: number; w: number; h: number } | null = null;
  /** Live FSR state (null = no upscale in this build). */
  private _upscale: { scale: number; mode: 'fsr' | 'taau'; easu: EASUNodeResult | null; rcas: RCASNodeResult | null; w: number; h: number } | null = null;
  /** Extra PassNodes this pipeline created (e.g. the transition's scene B pass) — disposed with it. */
  private _ownedPassNodes: unknown[] = [];
  private _setupPromise: Promise<void>;

  /** Access god rays result for per-frame sun position uniform updates. */
  get godRays(): GodRaysNodeResult | null { return this._godRays; }
  /** The lens-flare node result (uniforms: brightThreshold, ghost/starburst/streak intensities) or null. */
  get lensFlare(): LensFlareNodeResult | null { return this._lensFlare; }

  /**
   * Access contact shadows uniforms for per-frame light direction updates.
   * Update `contactShadowsUniforms.lightDirView.value` each frame using
   * `getPrimaryLightDirView(scene, camera)` from the shadows barrel.
   */
  get contactShadowsUniforms(): ContactShadowsUniforms | null { return this._contactShadowsUniforms; }

  /** Projection coefficients, near/far and the primary light direction feeding
   *  the contact-shadow march — nothing refreshed them before (no system was
   *  ever instantiated), so FOV/resize changes and the sun direction were ignored. */
  private _refreshContactShadowUniforms(): void {
    const u = this._contactShadowsUniforms;
    if (!u) return;
    const cam = this.camera as THREE.Camera & { near?: number; far?: number };
    const pm = cam.projectionMatrix.elements;
    u.m11.value = pm[0]; u.m22.value = pm[5]; u.m33.value = pm[10]; u.m43.value = pm[14];
    u.invM11.value = 1 / (pm[0] === 0 ? 1e-6 : pm[0]);
    u.invM22.value = 1 / (pm[5] === 0 ? 1e-6 : pm[5]);
    if (typeof cam.near === 'number') u.near.value = cam.near;
    if (typeof cam.far === 'number') u.far.value = cam.far;
    try { u.lightDirView.value.copy(getPrimaryLightDirView(this.scene, this.camera)); } catch { /* no light yet */ }
  }

  /** Resolves when async setup is complete. Useful for tests. */
  get ready(): Promise<void> { return this._setupPromise; }

  /** Aerial-perspective uniforms (camera params refresh via setCamera). */
  get aerialUniforms(): AerialCompositeUniforms | null { return this._aerialUniforms; }
  /** Vignette uniforms for runtime control. */
  get vignetteUniforms(): VignetteUniforms | null { return this._vignetteUniforms; }
  /** Lens-distortion uniforms for runtime control. */
  get lensDistortionUniforms(): LensDistortionUniforms | null { return this._lensDistortionUniforms; }
  /** Parametric colour-grade uniforms for runtime control. */
  get colorGradeUniforms(): ColorGradeUniforms | null { return this._colorGradeUniforms; }
  /** Sharpen uniforms for runtime control. */
  get sharpenUniforms(): SharpenUniforms | null { return this._sharpenUniforms; }
  /** Volumetric fog uniforms (sun dir, density, volumes) for runtime control. */
  get volumetricFogUniforms(): VolumetricFogUniforms | null { return this._volumetricFogUniforms; }
  /** Capsule-shadow uniforms (per-capsule slots, light dir) for runtime control. */
  get capsuleShadowsUniforms(): CapsuleShadowsUniforms | null { return this._capsuleShadowsUniforms; }

  /**
   * The HDR scene pass render target (null until setup completes). Consumers:
   * AutoExposureSystem reads luminance from it.
   */
  get scenePassRenderTarget(): { width: number; height: number } | null {
    return (this._scenePass as { renderTarget?: { width: number; height: number } } | null)
      ?.renderTarget ?? null;
  }

  constructor(
    renderer: THREE.WebGPURenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    config?: PostProcessingConfig,
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    this._setupPromise = this._setup(config);
  }

  private async _setup(config?: PostProcessingConfig): Promise<void> {
    try {
      // Tone-map config: three's PostProcessing applies renderer.toneMapping +
      // toneMappingExposure in its display-output transform, so the pipeline
      // consumes this config by writing renderer state. (Was declared but
      // never read — 2026-08-17 shader audit bug #2.)
      if (config?.tonemap) {
        const tm = config.tonemap.operator !== undefined
          ? toneMappingForOperator(config.tonemap.operator)
          : config.tonemap.toneMapping;
        if (tm !== undefined) this.renderer.toneMapping = tm;
        if (config.tonemap.exposure !== undefined) this.renderer.toneMappingExposure = config.tonemap.exposure;
        Logger.info('PostProcessing', `Tonemap applied (operator=${config.tonemap.operator ?? 'explicit'}, exposure=${this.renderer.toneMappingExposure})`);
      }

      // three r185 renamed PostProcessing → RenderPipeline (the old name logs a deprecation every construction).
      let PipelineCtor = THREE.PostProcessing;
      try { const rp = (THREE as unknown as { RenderPipeline?: typeof THREE.PostProcessing }).RenderPipeline; if (rp) PipelineCtor = rp; } catch { /* a mocked namespace without the r185 export */ }
      this.postProcessing = new PipelineCtor(this.renderer);

      // Create scene pass and extract texture nodes (Three.js r182 API)
      const scenePass = pass(this.scene, this.camera);
      this._scenePass = scenePass; // Store for setCamera updates
      const scenePassColor = (scenePass as any).getTextureNode('output');
      const scenePassDepth = (scenePass as any).getTextureNode('depth');

      // Only request MRT outputs when effects that consume them are enabled.
      // Calling getTextureNode('normal') etc. unconditionally tells PassNode
      // to add extra color attachments, which causes WebGPU validation errors
      // when standard materials don't output to those targets.
      const needsNormal = !!(config?.ao || config?.ssgi || config?.ssr || config?.denoise);
      const needsVelocity = !!(config?.motionBlur || config?.traa?.enabled || config?.upscale?.mode === 'taau');
      const needsMetalness = !!(config?.ssr);
      const needsRoughness = !!(config?.ssr);

      let scenePassNormal: any = null;
      let scenePassVelocity: any = null;
      let scenePassMetalness: any = null;
      let scenePassRoughness: any = null;
      if (needsNormal) try { scenePassNormal = (scenePass as any).getTextureNode('normal'); } catch { /* not available */ }
      if (needsVelocity) try { scenePassVelocity = (scenePass as any).getTextureNode('velocity'); } catch { /* not available */ }
      if (needsMetalness) try { scenePassMetalness = (scenePass as any).getTextureNode('metalness'); } catch { /* not available */ }
      if (needsRoughness) try { scenePassRoughness = (scenePass as any).getTextureNode('roughness'); } catch { /* not available */ }

      // THE MRT CONTRACT (r185): getTextureNode('velocity'|'normal'|…) only
      // ADDS the extra color attachment to the pass render target — it does
      // NOT make materials write it. Without pass.setMRT(mrt({...})),
      // renderer.getMRT() is null during the scene pass, every material
      // builds a single-output fragment shader, and Dawn rejects EVERY
      // pipeline against the multi-attachment pass ("Attachment state of
      // [RenderPipeline "Background.material"] is not compatible" — the
      // TRAA/motion-blur black screen). three's own postprocessing examples
      // always pair the two calls; mirror them with the canonical writer
      // node per output.
      if (scenePassNormal || scenePassVelocity || scenePassMetalness || scenePassRoughness) {
        try {
          const tsl = await import('three/tsl');
          if (this._disposed) return;
          const layout: Record<string, unknown> = { output: tsl.output };
          // Each surface output branches on the material being built: a marked
          // overlay (post/mrtOverlay.ts) writes alpha-0 zeros, everything else the
          // node it always wrote.
          if (scenePassNormal) layout.normal = mrtSurfaceOutput(tsl.transformedNormalView);
          if (scenePassVelocity) layout.velocity = mrtSurfaceOutput(tsl.velocity);
          if (scenePassMetalness) layout.metalness = mrtSurfaceOutput(tsl.metalness);
          if (scenePassRoughness) layout.roughness = mrtSurfaceOutput(tsl.roughness);
          const mrtNode = tsl.mrt(layout as Parameters<typeof tsl.mrt>[0]);
          // The surface targets blend by the MATERIAL's function (post/mrtOverlay.ts):
          // three's per-target default is NoBlending, which let every transparent
          // overlay REPLACE the ground's normal under it — GTAO's black squares
          // under footprints and light rectangles around rain (2026-09-17).
          const blended = installMrtOverlayBlending(mrtNode, Object.keys(layout));
          (scenePass as { setMRT: (m: unknown) => void }).setMRT(mrtNode);
          Logger.info('PostProcessing', `Scene pass MRT installed (${Object.keys(layout).join(', ')}; material blending on ${blended.join(', ') || 'none'})`);
        } catch (err) {
          Logger.warn('PostProcessing', `Scene pass MRT setup failed: ${err}`);
        }
      }

      // r185 STALE BACKGROUND PIPELINE guard: the scene pass's color-target
      // COUNT changes whenever a rebuild adds/removes an MRT output (velocity
      // for TRAA/motion blur, normal for AO/SSR). Regular scene materials
      // recompile against the new attachment state, but three's Background.js
      // only flags its internal NodeMaterial needsUpdate when the background
      // NODE's cache key changes — its cached single-target pipeline then
      // fails Dawn's strict attachment validation and the whole command
      // buffer dies (black screen: "Attachment state of [RenderPipeline
      // "Background.material"] is not compatible"). Wrapping the background
      // node in a FRESH vec4 changes its cache key, forcing the Background
      // path to rebuild its material against the CURRENT targets. Always wrap
      // the ORIGINAL node (tracked on the scene) so repeated rebuilds never
      // nest wrappers. Visually a no-op; runs once per pipeline (re)build.
      const sceneWithBg = this.scene as {
        backgroundNode?: unknown; userData?: Record<string, unknown>;
      };
      if (sceneWithBg.backgroundNode) {
        const ud = (sceneWithBg.userData ??= {});
        // If the app swapped in a NEW background node since our last wrap,
        // adopt it as the original — never resurrect a stale node.
        if (sceneWithBg.backgroundNode !== ud.__bgWrapperNode) {
          ud.__originalBackgroundNode = sceneWithBg.backgroundNode;
        }
        const wrapper = vec4(ud.__originalBackgroundNode as never);
        ud.__bgWrapperNode = wrapper;
        sceneWithBg.backgroundNode = wrapper;
      }

      let outputNode: unknown = scenePassColor;

      // ════════════════════════════════════════════════════════════════════
      // FSR 1 upscale (perf 2026-09-08): the scene pass renders at 1/scale of
      // the drawing buffer; EASU reconstructs the full-size image from the
      // pass texture and RCAS sharpens it. Everything below sees full size.
      // The pass's depth stays at render resolution — depth consumers sample
      // it by screenUV, which is what they did at native anyway.
      // ════════════════════════════════════════════════════════════════════
      this._upscale = null;
      this._disposeCloudRtt();
      if (config?.upscale && FSR_PRESETS[config.upscale.quality]) {
        const preset = FSR_PRESETS[config.upscale.quality];
        const scale = preset.scaleFactor;
        const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
        const w = Math.max(1, Math.round(size.x)), h = Math.max(1, Math.round(size.y));
        (scenePass as { setResolutionScale?: (s: number) => void }).setResolutionScale?.(1 / scale);
        if (config.upscale.mode === 'taau') {
          // Temporal: the resolve happens at the AA slot (TRAA over the low-res
          // pass, output-resolution history); RCAS is appended there.
          this._upscale = { scale, mode: 'taau', easu: null, rcas: null, w, h };
          Logger.info('PostProcessing', `TAAU upscale ${config.upscale.quality} (${scale}×: ${Math.round(w / scale)}×${Math.round(h / scale)} → ${w}×${h}, temporal resolve at the AA slot)`);
        } else {
          // WHY the pass texture NODE, never its `.value`: a PassNode renders
          // only when a PassTextureNode referencing it is in the built graph.
          // Sampling the raw render-target texture left the scene pass out of
          // the frame entirely — a black picture on both backends (2026-09-08).
          const easu = createEASUNode({ srcColor: scenePassColor, srcWidth: Math.max(1, Math.round(w / scale)), srcHeight: Math.max(1, Math.round(h / scale)), dstWidth: w, dstHeight: h });
          let rcas: RCASNodeResult | null = null;
          outputNode = easu.node;
          const sharp = config.upscale.sharpness ?? preset.defaultSharpness;
          if (sharp > 0) {
            rcas = createRCASNode({ srcColor: convertToTexture(easu.node as never) as never, srcWidth: w, srcHeight: h, sharpness: sharp });
            outputNode = rcas.node;
          }
          this._upscale = { scale, mode: 'fsr', easu, rcas, w, h };
          this.passes.set('upscale', { name: 'upscale', node: outputNode, enabled: true });
          Logger.info('PostProcessing', `FSR 1 upscale ${config.upscale.quality} (${scale}×: ${Math.round(w / scale)}×${Math.round(h / scale)} → ${w}×${h}, sharpness ${sharp.toFixed(2)})`);
        }
      }

      // ════════════════════════════════════════════════════════════════════
      // 0. Volumetric clouds — depth-aware composite over SKY pixels only.
      //    Done FIRST (on the raw scene colour) so the clouds flow through the
      //    rest of the HDR chain (god rays / bloom / tone-map) exactly like the
      //    sky behind them. The cloud node ray-marches from the real camera
      //    position, so it is world-anchored + parallaxing; the sky mask
      //    (render/depth/skyMask.ts — the depth clear value, shared with
      //    GodRaysPass) keeps opaque geometry in front of the clouds.
      //        composited = sceneColor·(1 − a) + rgb        (over)
      //        final      = mix(sceneColor, composited, skyMask)
      // ════════════════════════════════════════════════════════════════════
      // Reusable composite: applied at slot 0 (default) or after AA
      // (clouds.afterAA — the TRAA interaction, see CloudsPassConfig).
      // Cloud raymarch at a FRACTION of the drawing buffer (perf 2026-09-08):
      // the RGBA cloud node renders once per frame into an RTT sized by
      // `clouds.resolutionScale` (0.5 = a quarter of the rays) and the
      // composite samples that texture bilinearly — a soft, low-frequency
      // volume upsampled cleanly; the temporal resolve / upscaler smooths it.
      let cloudSrc: { rgb: unknown; a: unknown } | null = null;
      if (config?.clouds?.node && config.clouds.enabled !== false) {
        const rs = config.clouds.resolutionScale;
        if (rs !== undefined && rs > 0 && rs < 1) {
          try {
            const { rtt } = await import('three/tsl');
            const n = config.clouds.node;
            const size0 = this.renderer.getDrawingBufferSize(new THREE.Vector2());
            const tex = rtt(vec4((n.rgb as never), (n.a as never)) as never, Math.max(1, Math.round(size0.x * rs)), Math.max(1, Math.round(size0.y * rs))) as unknown as { autoResize?: boolean; setSize?: (w: number, h: number) => void; rgb: unknown; a: unknown };
            tex.autoResize = false; // sized by _refreshCloudRtt at rs × the drawing buffer
            this._cloudRtt = { node: tex, rs, w: size0.x, h: size0.y };
            cloudSrc = { rgb: tex.rgb, a: tex.a };
            Logger.info('PostProcessing', `Clouds raymarch at ${rs}× the drawing buffer (RTT)`);
          } catch (err) { Logger.warn('PostProcessing', `Cloud RTT unavailable, full-res raymarch: ${err}`); }
        }
      }
      const compositeClouds = (input: unknown): unknown => {
        const cloud = cloudSrc ?? config!.clouds!.node;
        // Sky mask: 1 where nothing was drawn, 0 on geometry — the helper also
        // does the `.r` reduction (a vec4 depth sample into step() is a WGSL /
        // GLSL codegen type mismatch). WHY the helper: the old
        // `step(0.999, depth)` is z ≈ near/0.001 = 50 m on Atlas's camera, so
        // the clouds composited over all geometry past 50 m instead of over
        // sky only. See render/depth/skyMask.ts.
        const mask = skyMask(scenePassDepth);
        const sceneColor = input as { mul: (n: unknown) => { add: (n: unknown) => unknown } };
        const oneMinusA = (cloud.a as { oneMinus: () => unknown }).oneMinus();
        const composited = sceneColor.mul(oneMinusA).add(cloud.rgb);
        return mix(input as never, composited as never, mask as never);
      };
      if (config?.clouds?.node && config.clouds.enabled !== false && !config.clouds.afterAA) {
        try {
          outputNode = compositeClouds(outputNode);
          this.passes.set('clouds', { name: 'clouds', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Volumetric clouds pass enabled (depth-aware sky composite)');
        } catch (err) {
          Logger.warn('PostProcessing', `Clouds pass failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 0b. Aerial perspective — froxel haze applied to GEOMETRY pixels on the
      //     raw HDR colour (sky pixels masked out: the sky-view LUT already
      //     carries full-path scattering). Closes the "baked but never
      //     composited" gap from the 2026-08-17 shader audit.
      // ════════════════════════════════════════════════════════════════════
      if (config?.aerial?.volume && config.aerial.enabled !== false) {
        try {
          const ap = createAerialCompositeNode(
            outputNode, scenePassDepth, this.camera, config.aerial,
          );
          outputNode = ap.node;
          this._aerialUniforms = ap.uniforms;
          this.passes.set('aerial', { name: 'aerial', node: ap.node, enabled: true });
          Logger.info('PostProcessing', 'Aerial-perspective composite enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Aerial composite failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 0c. Volumetric fog — analytic height fog + sun in-scatter + local
      //     volumes, on the HDR colour so the fog blooms/tone-maps with the
      //     scene it veils.
      // ════════════════════════════════════════════════════════════════════
      if (config?.volumetricFog && config.volumetricFog.enabled !== false) {
        try {
          const fog = createVolumetricFogNode(
            outputNode, scenePassDepth, this.camera, config.volumetricFog,
          );
          outputNode = fog.node;
          this._volumetricFogUniforms = fog.uniforms;
          this.passes.set('volumetricFog', { name: 'volumetricFog', node: fog.node, enabled: true });
          Logger.info('PostProcessing', 'Volumetric fog pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Volumetric fog failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 1. SSAO/AO — ao(depthNode, normalNode, camera) — multiplicative
      // ════════════════════════════════════════════════════════════════════
      if (config?.ao) {
        try {
          const { ao } = await import('three/examples/jsm/tsl/display/GTAONode.js');
          if (this._disposed) return;
          const aoPass = ao(scenePassDepth, scenePassNormal, this.camera);
          // Perf 2026-09-08: GTAO at a fraction of the drawing buffer (three's GTAONode.resolutionScale).
          if (config.ao.resolutionScale !== undefined) (aoPass as { resolutionScale?: number }).resolutionScale = Math.max(0.25, Math.min(1, config.ao.resolutionScale));
          // Render wave 1 (2026-09-17): the tier's radius/samples reach the node
          // (only resolutionScale did), and `intensity` is HOW MUCH of the
          // occlusion applies (mix toward 1), not a raw multiplier.
          if (config.ao.radius !== undefined && (aoPass as any).radius) (aoPass as any).radius.value = config.ao.radius;
          if (config.ao.samples !== undefined && (aoPass as any).samples) (aoPass as any).samples.value = config.ao.samples;
          const aoTex: any = (aoPass as any).getTextureNode();
          const aoTerm = mix(float(1), aoTex.r, float(Math.max(0, Math.min(1, config.ao.intensity ?? 1))));
          outputNode = vec4((outputNode as any).rgb.mul(aoTerm), (outputNode as any).a);
          this.passes.set('ao', { name: 'ao', node: aoPass, enabled: true });
          Logger.info('PostProcessing', 'AO (GTAO) pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'AO pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 2. SSGI — ssgi(beautyNode, depthNode, normalNode, camera)
      // ════════════════════════════════════════════════════════════════════
      if (config?.ssgi) {
        try {
          const { ssgi } = await import('three/examples/jsm/tsl/display/SSGINode.js');
          if (this._disposed) return;
          const ssgiPass = ssgi(outputNode as any, scenePassDepth, scenePassNormal, this.camera as any);
          // three r185 SSGINode: getAONode()/getGINode(); the example composites
          // colour·ao + diffuse·gi — the scene colour stands in for the diffuse MRT.
          if (config.ssgi.sliceCount !== undefined && (ssgiPass as any).sliceCount) (ssgiPass as any).sliceCount.value = config.ssgi.sliceCount;
          if (config.ssgi.stepCount !== undefined && (ssgiPass as any).stepCount) (ssgiPass as any).stepCount.value = config.ssgi.stepCount;
          if (config.ssgi.temporal !== undefined) (ssgiPass as any).useTemporalFiltering = config.ssgi.temporal;
          const aoN = (ssgiPass as any).getAONode(), giN = (ssgiPass as any).getGINode();
          const aoTerm = mix(float(1), aoN.r, float(config.ssgi.aoStrength ?? 0.5)); // full-strength SSGI AO halved a potato frame's brightness (probe 2026-09-09)
          const lit = (outputNode as any).rgb.mul(aoTerm).add((outputNode as any).rgb.mul(giN.rgb).mul(config.ssgi.intensity ?? 1));
          outputNode = vec4(lit, (outputNode as any).a);
          this.passes.set('ssgi', { name: 'ssgi', node: ssgiPass, enabled: true });
          Logger.info('PostProcessing', 'SSGI pass enabled');
        } catch (e) {
          Logger.warn('PostProcessing', `SSGI pass not available — skipping: ${String(e).slice(0, 160)}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 3. SSR — ssr(color, depth, normal, metalness, roughness, camera)
      // ════════════════════════════════════════════════════════════════════
      if (config?.ssr) {
        const webgpu = !!(this.renderer as { backend?: { isWebGPUBackend?: boolean } }).backend?.isWebGPUBackend;
        if (!webgpu) {
          // three r185's SSRNode does not compile on the WebGL2 backend (GLSL
          // 'l-value'/cast errors in its march, probe 2026-09-09); it is developed
          // on WebGPU, where it draws cleanly. Skip rather than log a shader error
          // every frame.
          Logger.warn('PostProcessing', 'SSR needs the WebGPU backend — skipped on WebGL2');
        } else if (!scenePassNormal || !scenePassMetalness) {
          Logger.warn('PostProcessing', 'SSR requires MRT (normal + metalness) — skipping. Set up MRT on the scene pass.');
        } else {
          try {
            const { ssr } = await import('three/examples/jsm/tsl/display/SSRNode.js');
            if (this._disposed) return;
            // three r185: ssr(color, depth, normal, { metalnessNode, roughnessNode, camera }) — the
            // per-pixel PBR inputs ride the options object and the result is ADDED to the
            // scene colour (the r185 example: scenePassColor.add(ssrPass.rgb)). The older
            // positional call threw here and the pass was silently "not available".
            // SSRNode samples its colour input (`this.colorNode.sample`): hand it a texture NODE.
            const ssrPass: any = (ssr as any)(convertToTexture(outputNode as never), scenePassDepth, scenePassNormal, {
              metalnessNode: scenePassMetalness,
              roughnessNode: scenePassRoughness,
              camera: this.camera,
            });
            if (config.ssr.intensity !== undefined && ssrPass.intensity) ssrPass.intensity.value = config.ssr.intensity;
            if (config.ssr.maxDistance !== undefined && ssrPass.maxDistance) ssrPass.maxDistance.value = config.ssr.maxDistance;
            if (config.ssr.thickness !== undefined && ssrPass.thickness) ssrPass.thickness.value = config.ssr.thickness;
            if (config.ssr.resolutionScale !== undefined) ssrPass.resolutionScale = config.ssr.resolutionScale;
            // SSR OWNERSHIP (render wave 1, 2026-09-17): a HIT replaces the beauty's
            // indirect specular by metalness instead of stacking on it — the r185
            // example adds, and a metal or a puddle then reflected twice (the sky
            // PMREM once in the scene pass, the ray once more). The node's alpha is
            // the hit's world distance (0 on a miss). post/ssrComposite.ts is the
            // CPU mirror the unit test pins.
            const ssrTex: any = ssrPass.getTextureNode();
            const ssrHitN = step(float(SSR_HIT_EPS), ssrTex.a);
            const ssrW = ssrHitN.mul(clamp(float(scenePassMetalness as never), 0, 1)).mul(float(SSR_REPLACE));
            const ssrRgb = (outputNode as any).rgb.mul(float(1).sub(ssrW)).add(ssrTex.rgb);
            outputNode = vec4(ssrRgb, (outputNode as any).a);
            this.passes.set('ssr', { name: 'ssr', node: ssrPass, enabled: true });
            Logger.info('PostProcessing', 'SSR pass enabled');
          } catch (e) {
            Logger.warn('PostProcessing', `SSR pass not available — skipping: ${String(e).slice(0, 160)}`);
          }
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 4. Denoise — denoise(textureNode, depthNode, normalNode, camera)
      // ════════════════════════════════════════════════════════════════════
      if (config?.denoise) {
        try {
          const { denoise } = await import('three/examples/jsm/tsl/display/DenoiseNode.js');
          if (this._disposed) return;
          const denoisePass = denoise(outputNode as any, scenePassDepth, scenePassNormal, this.camera);
          outputNode = denoisePass;
          this.passes.set('denoise', { name: 'denoise', node: denoisePass, enabled: true });
          Logger.info('PostProcessing', 'Denoise pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Denoise pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 4b. Contact Shadows — screen-space ray-march, pre-tonemap multiplicative
      //     Darkens the scene colour where the depth buffer indicates occlusion
      //     along the primary directional light direction.
      //     Placed BEFORE God Rays and Bloom so that shadowed regions don't
      //     bleed bright halo artefacts into adjacent lit areas.
      // ════════════════════════════════════════════════════════════════════
      if (config?.contactShadows && config.contactShadows.enabled !== false) {
        try {
          const csResult = createContactShadowsNode(
            outputNode,
            scenePassDepth,
            this.camera,
            config.contactShadows,
          );
          outputNode = csResult.node;
          this._contactShadowsUniforms = csResult.uniforms;
          this.passes.set('contactShadows', { name: 'contactShadows', node: csResult.node, enabled: true });

          // Seed the light direction (the result used to be computed and thrown
          // away — the uniform stayed at (0,1,0)); render() refreshes it per frame.
          this._refreshContactShadowUniforms();

          Logger.info('PostProcessing', `Contact shadows pass enabled (samples=${csResult.config.sampleCount})`);
        } catch (err) {
          Logger.warn('PostProcessing', `Contact shadows pass failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 4c. Capsule Shadows — analytic character-proxy occlusion, after
      //     contact shadows on the same pre-tonemap colour.
      // ════════════════════════════════════════════════════════════════════
      if (config?.capsuleShadows && config.capsuleShadows.enabled !== false) {
        try {
          const cs = createCapsuleShadowsNode(
            outputNode, scenePassDepth, this.camera, config.capsuleShadows,
          );
          outputNode = cs.node;
          this._capsuleShadowsUniforms = cs.uniforms;
          this.passes.set('capsuleShadows', { name: 'capsuleShadows', node: cs.node, enabled: true });
          Logger.info('PostProcessing', 'Capsule shadows pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Capsule shadows failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 5. God Rays — additive, composited before bloom so rays get bloom'd
      // ════════════════════════════════════════════════════════════════════
      if (config?.godRays && config.godRays.enabled !== false) {
        try {
          const godRaysResult = createGodRaysNode(
            scenePassColor, scenePassDepth, config.godRays,
          );
          outputNode = (outputNode as any).add(godRaysResult.node);
          this._godRays = godRaysResult;
          this.passes.set('godRays', { name: 'godRays', node: godRaysResult.node, enabled: true });
          Logger.info('PostProcessing', 'God rays pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'God rays pass not available');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 6. DOF — dof(node, viewZNode, focusDistance, focalLength, bokehScale)
      // ════════════════════════════════════════════════════════════════════
      if (config?.dof) {
        try {
          const { dof } = await import('three/examples/jsm/tsl/display/DepthOfFieldNode.js');
          if (this._disposed) return;
          // Three.js DOF expects a view-space Z node (negative, in metres),
          // NOT raw 0..1 device depth. Passing scenePassDepth directly made
          // the circle-of-confusion radius wrong at every distance except
          // exactly the focus plane. Convert via the matching helper for
          // the camera projection type.
          // Use `uniform()` (not `float()`) so changes to the camera's
          // near/far (zoom, cutscene swap, setCamera()) propagate. `float()`
          // creates a CONSTANT TSL node that bakes the construction-time
          // values into the shader — same class of bug as ContactShadowsNode
          // pre-pass-1. Uniforms are stored on the instance so setCamera()
          // and the per-frame render hook can refresh `.value`.
          const cam = this.camera as THREE.PerspectiveCamera & THREE.OrthographicCamera;
          const isOrtho = (this.camera as { isOrthographicCamera?: boolean }).isOrthographicCamera === true;
          this._dofNearUniform = uniform(cam.near);
          this._dofFarUniform = uniform(cam.far);
          const viewZNode = isOrtho
            ? orthographicDepthToViewZ(scenePassDepth as never, this._dofNearUniform, this._dofFarUniform)
            : perspectiveDepthToViewZ(scenePassDepth as never, this._dofNearUniform, this._dofFarUniform);
          if (config.dof.bokeh === 'hex') {
            // The custom hex-aperture bokeh (audit: BokehDofNode was never
            // wired). bokehScale maps onto the CoC pixel cap.
            //
            // The hex path's model is `coc_px = cocScale·(1 − focusZ/z)`, a
            // PIXEL scale, where three's standard path takes `focalLength` as a
            // world-unit focus RANGE. One config field, two incompatible
            // meanings — the old `focalLength · 200` only produced a sane pixel
            // scale because `focalLength` was being set as if it were a lens
            // length in metres. Derive the pixel scale from the range instead,
            // inversely: a wide focus range means less blur (2026-09-14).
            const { createBokehDofNode } = await import('./dof/BokehDofNode.js');
            const { createBokehDofConfig } = await import('./dof/BokehDofConfig.js');
            if (this._disposed) return;
            const focusRange = Math.max(0.5, config.dof.focalLength ?? 40);
            const hexCfg = createBokehDofConfig({
              focusDistance: config.dof.focusDistance ?? 10,
              cocScale: Math.max(2, Math.min(32, 600 / focusRange)),
              maxCocPixels: (config.dof.bokehScale ?? 5) * 3.2,
            });
            // Earlier passes replace outputNode with an expression node; the
            // bokeh samples it with texture(), which throws on anything but a
            // Texture/TextureNode — render it to a texture first.
            const hex = createBokehDofNode({
              sceneColor: convertToTexture(outputNode as never) as never, viewZNode, config: hexCfg,
            });
            outputNode = hex.node;
            this.passes.set('dof', { name: 'dof', node: hex.node, enabled: true });
            Logger.info('PostProcessing', 'DOF pass enabled (hex bokeh)');
          } else {
            const dofPass = (dof as any)(
              outputNode,
              viewZNode,
              config.dof.focusDistance ?? 10,
              config.dof.focalLength ?? 40, // focus RANGE in world units — see DOFConfig
              config.dof.bokehScale ?? 5,
            );
            outputNode = dofPass;
            this.passes.set('dof', { name: 'dof', node: dofPass, enabled: true });
            Logger.info('PostProcessing', 'DOF pass enabled');
          }
        } catch {
          Logger.warn('PostProcessing', 'DOF pass not available — skipping');
        }
      }

      // ════════════════════════════════════════════════════════════════════
      // 10b. Lens flares — pseudo (screen-space) flares from the frame's bright
      //      pixels; sampled through the texture NODE (the pass law).
      // ════════════════════════════════════════════════════════════════════
      this._lensFlare = null;
      if (config?.lensFlares && config.lensFlares.enabled !== false) {
        try {
          // three r185's first-party screen-space flare (ghosts pivoting around
          // the centre from the frame's bright pixels — the pseudo-lens-flare
          // family). The engine's own LensFlareNode emitted an invalid GLSL
          // literal on WebGL2 (2026-09-09 probe), so the maintained node is the
          // wiring; the engine config maps onto its params.
          const { lensflare } = await import('three/examples/jsm/tsl/display/LensflareNode.js');
          if (this._disposed) return;
          const cfg = createLensFlareConfig(config.lensFlares);
          const flare: any = (lensflare as any)(outputNode as any, {
            threshold: cfg.brightThreshold,
            ghostSamples: 4,
            ghostSpacing: 0.25,
            ghostAttenuationFactor: 25,
            ghostTint: new THREE.Color(cfg.streakColor.r, cfg.streakColor.g, cfg.streakColor.b),
            downSampleRatio: 4,
          });
          const gain = cfg.ghostIntensity;
          outputNode = (outputNode as any).add(flare.rgb.mul(gain));
          this._lensFlare = { node: flare, uniforms: { threshold: flare.threshold ?? null, gain } } as unknown as LensFlareNodeResult;
          this.passes.set('lensFlares', { name: 'lensFlares', node: flare, enabled: true });
          Logger.info('PostProcessing', 'Lens flares (screen-space pseudo flare) enabled');
        } catch (e) {
          Logger.warn('PostProcessing', `Lens flares unavailable: ${String(e).slice(0, 160)}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 7. Motion Blur — motionBlur(inputNode, velocity, numSamples)
      // ════════════════════════════════════════════════════════════════════
      if (config?.motionBlur) {
        if (!scenePassVelocity) {
          Logger.warn('PostProcessing', 'Motion blur requires velocity MRT — skipping. Set up MRT with velocity output.');
        } else {
          try {
            // WHY not three's stock motionBlur(): it samples only along the
            // pixel's OWN velocity, applies NDC motion as UV (2× too long),
            // never clamps (a camera flick = full-screen streaks), has no
            // depth awareness and a fixed tap lattice — the "glitchy" look.
            // The reconstruction filter (tile-max → neighbour-max → jittered
            // depth-weighted gather, frame-rate-normalised shutter) is the
            // McGuire 2012 / Jimenez 2014 standard.
            const { MotionBlurNode } = await import('./post/MotionBlurNode.js');
            if (this._disposed) return;
            const mb = config.motionBlur;
            const mbPass = new MotionBlurNode(outputNode, scenePassVelocity, scenePassDepth ?? null, this.camera, {
              samples: mb.samples ?? 16, intensity: mb.intensity ?? 1, maxBlurPx: mb.maxBlurPx ?? 48,
              tileSize: mb.tileSize ?? 20, depthAware: mb.depthAware ?? true,
            });
            outputNode = mbPass as unknown as typeof outputNode;
            this.passes.set('motionBlur', { name: 'motionBlur', node: mbPass, enabled: true });
            Logger.info('PostProcessing', `Motion blur pass enabled (reconstruction filter: ${mb.samples ?? 16} taps, intensity ${mb.intensity ?? 1}, max ${mb.maxBlurPx ?? 48} px)`);
          } catch (err) {
            Logger.warn('PostProcessing', `Motion blur pass not available — skipping (${err})`);
          }
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 8. Bloom — bloom(node, strength, radius, threshold) — additive
      // ════════════════════════════════════════════════════════════════════
      if (config?.bloom && config.bloom.mode === 'kawase') {
        // Dual-Kawase bloom (stage 4): rtt levels sampled through their nodes.
        try {
          const { dualKawaseBloom } = await import('./post/DualKawaseBloom.js');
          if (this._disposed) return;
          const size = this._drawingBufferSize();
          const kb = dualKawaseBloom(outputNode, {
            width: size.x, height: size.y,
            strength: config.bloom.strength ?? 0.5,
            threshold: config.bloom.threshold ?? 0.8,
            radius: config.bloom.radius ?? 1,
            passes: config.bloom.passes ?? 2,
          });
          outputNode = (outputNode as any).add(kb.node);
          this.passes.set('bloom', { name: 'bloom', node: kb.node, enabled: true });
          this._kawase = kb;
          Logger.info('PostProcessing', `Bloom: dual-Kawase (${config.bloom.passes ?? 2} down/up pairs) at ${size.x}×${size.y}`);
        } catch (err) {
          Logger.warn('PostProcessing', `Dual-Kawase bloom not available: ${err}`);
        }
      } else if (config?.bloom) {
        try {
          const { bloom } = await import('three/examples/jsm/tsl/display/BloomNode.js');
          if (this._disposed) return;
          const bloomPass = bloom(
            outputNode as any,
            config.bloom.strength ?? 1.5,
            config.bloom.radius ?? 0.4,
            config.bloom.threshold ?? 0.8,
          );
          outputNode = (outputNode as any).add(bloomPass);
          this.passes.set('bloom', { name: 'bloom', node: bloomPass, enabled: true });
        } catch {
          Logger.warn('PostProcessing', 'Bloom pass not available');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 9. Anamorphic — anamorphic(node, threshold, scale, samples) — additive
      // ════════════════════════════════════════════════════════════════════
      if (config?.anamorphic) {
        try {
          // r185 removed AnamorphicNode upstream — vendored copy (see vendor/AnamorphicNode.js).
          const { anamorphic } = await import('./vendor/AnamorphicNode.js');
          if (this._disposed) return;
          const anamorphicPass = (anamorphic as any)(
            outputNode,
            config.anamorphic.threshold ?? 0.9,
            config.anamorphic.scale ?? 3,
            config.anamorphic.samples ?? 32,
          );
          outputNode = (outputNode as any).add(anamorphicPass);
          this.passes.set('anamorphic', { name: 'anamorphic', node: anamorphicPass, enabled: true });
          Logger.info('PostProcessing', 'Anamorphic pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Anamorphic pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 10. After Image — afterImage(node, damp) — temporal trail
      // ════════════════════════════════════════════════════════════════════
      if (config?.afterImage) {
        try {
          const { afterImage } = await import('three/examples/jsm/tsl/display/AfterImageNode.js');
          if (this._disposed) return;
          const aiPass = afterImage(outputNode as any, config.afterImage.damp ?? 0.96);
          outputNode = aiPass;
          this.passes.set('afterImage', { name: 'afterImage', node: aiPass, enabled: true });
          Logger.info('PostProcessing', 'After image pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'After image pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 11. Outline — outline(scene, camera, selectedObjects) — additive
      // ════════════════════════════════════════════════════════════════════
      if (config?.outline) {
        try {
          const { outline } = await import('three/examples/jsm/tsl/display/OutlineNode.js');
          if (this._disposed) return;
          const outlinePass = (outline as any)(this.scene, this.camera, config.outline.selectedObjects);
          outputNode = (outputNode as any).add(outlinePass);
          this.passes.set('outline', { name: 'outline', node: outlinePass, enabled: true });
          Logger.info('PostProcessing', 'Outline pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Outline pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 12. Film — film(inputNode, intensityNode) — grain overlay
      // ════════════════════════════════════════════════════════════════════
      if (config?.film) {
        try {
          const { film } = await import('three/examples/jsm/tsl/display/FilmNode.js');
          if (this._disposed) return;
          const filmPass = (film as any)(outputNode, config.film.intensity ?? 1);
          outputNode = filmPass;
          this.passes.set('film', { name: 'film', node: filmPass, enabled: true });
          Logger.info('PostProcessing', 'Film grain pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Film pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 13. Dot Screen — dotScreen(node, angle, scale) — halftone
      // ════════════════════════════════════════════════════════════════════
      if (config?.dotScreen) {
        try {
          const { dotScreen } = await import('three/examples/jsm/tsl/display/DotScreenNode.js');
          if (this._disposed) return;
          const dotPass = (dotScreen as any)(outputNode, config.dotScreen.angle ?? 1.57, config.dotScreen.scale ?? 1);
          outputNode = dotPass;
          this.passes.set('dotScreen', { name: 'dotScreen', node: dotPass, enabled: true });
          Logger.info('PostProcessing', 'Dot screen pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Dot screen pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 14. RGB Shift — rgbShift(node, amount, angle) — color fringing
      // ════════════════════════════════════════════════════════════════════
      if (config?.rgbShift) {
        try {
          const { rgbShift } = await import('three/examples/jsm/tsl/display/RGBShiftNode.js');
          if (this._disposed) return;
          const rgbPass = (rgbShift as any)(outputNode, config.rgbShift.amount ?? 0.005, config.rgbShift.angle ?? 0);
          outputNode = rgbPass;
          this.passes.set('rgbShift', { name: 'rgbShift', node: rgbPass, enabled: true });
          Logger.info('PostProcessing', 'RGB shift pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'RGB shift pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 15. Chromatic Aberration — chromaticAberration(node, strength, center, scale)
      // ════════════════════════════════════════════════════════════════════
      if (config?.chromaticAberration) {
        try {
          const { chromaticAberration } = await import('three/examples/jsm/tsl/display/ChromaticAberrationNode.js');
          if (this._disposed) return;
          // WHY an explicit center: three r185's factory does nodeObject(center)
          // and nodeObject(null) === null — the documented "null = screen
          // center" default is NOT implemented, so a null reached the shader
          // Fn and produced an invalid program: a silent black frame on
          // WebGPU with no JS error (field-hit 2026-08-20).
          const caPass = (chromaticAberration as any)(
            outputNode,
            config.chromaticAberration.strength ?? 1.0,
            vec2(0.5, 0.5), // screen center
            config.chromaticAberration.scale ?? 1.1,
          );
          outputNode = caPass;
          this.passes.set('chromaticAberration', { name: 'chromaticAberration', node: caPass, enabled: true });
          Logger.info('PostProcessing', 'Chromatic aberration pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Chromatic aberration pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 16. Sepia — sepia(color) — warm color grading
      // ════════════════════════════════════════════════════════════════════
      if (config?.sepia) {
        try {
          const { sepia } = await import('three/examples/jsm/tsl/display/Sepia.js');
          if (this._disposed) return;
          outputNode = sepia(outputNode as any);
          this.passes.set('sepia', { name: 'sepia', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Sepia pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Sepia pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 17. Grayscale — grayscale(color) — desaturation
      // ════════════════════════════════════════════════════════════════════
      if (config?.grayscale) {
        try {
          const tsl = await import('three/tsl');
          if (this._disposed) return;
          if (typeof (tsl as any).grayscale === 'function') {
            outputNode = (tsl as any).grayscale(outputNode as any);
          } else if (typeof (tsl as any).luminance === 'function') {
            // Fallback: luminance is a common grayscale conversion
            outputNode = (tsl as any).luminance(outputNode as any);
          }
          this.passes.set('grayscale', { name: 'grayscale', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Grayscale pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Grayscale pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 17b. Parametric colour grade — lift/gamma/gain + saturation, applied
      //      BEFORE the 3D LUT so a baked LUT grades on top of the parametric
      //      base (the conventional grade → LUT order).
      // ════════════════════════════════════════════════════════════════════
      if (config?.colorGrade && config.colorGrade.enabled !== false) {
        try {
          const grade = createColorGradeNode(outputNode, config.colorGrade);
          outputNode = grade.node;
          this._colorGradeUniforms = grade.uniforms;
          this.passes.set('colorGrade', { name: 'colorGrade', node: grade.node, enabled: true });
          Logger.info('PostProcessing', 'Parametric colour grade enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Colour grade failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 18. LUT 3D — lut3D(node, lut, size, intensity) — color grading
      // ════════════════════════════════════════════════════════════════════
      if (config?.lut3D) {
        try {
          const { lut3D } = await import('three/examples/jsm/tsl/display/Lut3DNode.js');
          if (this._disposed) return;
          const lutPass = (lut3D as any)(
            outputNode,
            texture3D(config.lut3D.lut as never), // a texture NODE — lut3D calls .sample() on it (a raw texture broke: 'lutNode.sample is not a function')
            config.lut3D.size,
            config.lut3D.intensity ?? 1,
          );
          outputNode = lutPass;
          this.passes.set('lut3D', { name: 'lut3D', node: lutPass, enabled: true });
          Logger.info('PostProcessing', 'LUT 3D pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'LUT 3D pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 19. Sobel — sobel(node) — edge detection
      // ════════════════════════════════════════════════════════════════════
      if (config?.sobel) {
        try {
          const { sobel } = await import('three/examples/jsm/tsl/display/SobelOperatorNode.js');
          if (this._disposed) return;
          outputNode = sobel(outputNode as any);
          this.passes.set('sobel', { name: 'sobel', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Sobel edge detection pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Sobel pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 20. Gaussian Blur — gaussianBlur(node, direction, sigma, options)
      // ════════════════════════════════════════════════════════════════════
      if (config?.gaussianBlur) {
        try {
          const { gaussianBlur } = await import('three/examples/jsm/tsl/display/GaussianBlurNode.js');
          if (this._disposed) return;
          const gbPass = (gaussianBlur as any)(
            outputNode,
            null, // direction — null for both H+V passes
            config.gaussianBlur.sigma ?? 4,
            { resolution: config.gaussianBlur.resolutionScale ?? 1 },
          );
          outputNode = gbPass;
          this.passes.set('gaussianBlur', { name: 'gaussianBlur', node: gbPass, enabled: true });
          Logger.info('PostProcessing', 'Gaussian blur pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Gaussian blur pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 21. Box Blur — boxBlur(textureNode, options)
      // ════════════════════════════════════════════════════════════════════
      if (config?.boxBlur) {
        try {
          // boxBlur may be in three/tsl core or in display addons
          let boxBlurFn: any = null;
          try {
            const tsl = await import('three/tsl');
            boxBlurFn = (tsl as any).boxBlur;
          } catch { /* not in core */ }
          if (!boxBlurFn) {
            try {
              const mod = await import('three/examples/jsm/tsl/display/GaussianBlurNode.js');
              boxBlurFn = (mod as any).boxBlur;
            } catch { /* not in GaussianBlurNode */ }
          }
          if (this._disposed) return;
          if (boxBlurFn) {
            outputNode = boxBlurFn(outputNode as any, {});
            this.passes.set('boxBlur', { name: 'boxBlur', node: outputNode, enabled: true });
            Logger.info('PostProcessing', 'Box blur pass enabled');
          } else {
            Logger.warn('PostProcessing', 'Box blur function not found — skipping');
          }
        } catch {
          Logger.warn('PostProcessing', 'Box blur pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 22. Hash Blur — hashBlur(textureNode, amount, options)
      // ════════════════════════════════════════════════════════════════════
      if (config?.hashBlur) {
        try {
          const { hashBlur } = await import('three/examples/jsm/tsl/display/hashBlur.js');
          if (this._disposed) return;
          outputNode = (hashBlur as any)(outputNode, config.hashBlur.amount ?? 0.1, {});
          this.passes.set('hashBlur', { name: 'hashBlur', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Hash blur pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Hash blur pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 23. Transition — transition(nodeA, nodeB, mixTexture, mixRatio, threshold, useTexture)
      // ════════════════════════════════════════════════════════════════════
      if (config?.transition) {
        try {
          const { transition } = await import('three/examples/jsm/tsl/display/TransitionNode.js');
          if (this._disposed) return;
          const passB = pass(config.transition.sceneB, config.transition.cameraB);
          this._ownedPassNodes.push(passB); // owns a render target — freed in dispose()
          const colorB = (passB as any).getTextureNode('output');
          let mixTextureNode: any = null;
          if (config.transition.mixTexture) {
            // Create a texture node from the provided mix texture
            const tsl = await import('three/tsl');
            if ((tsl as any).texture) {
              mixTextureNode = (tsl as any).texture(config.transition.mixTexture);
            }
          }
          const transNode = (transition as any)(
            outputNode,
            colorB,
            mixTextureNode,
            config.transition.mixRatio ?? 0,
            config.transition.threshold ?? 0.1,
            config.transition.useTexture ? 1 : 0,
          );
          outputNode = transNode;
          this.passes.set('transition', { name: 'transition', node: transNode, enabled: true });
          Logger.info('PostProcessing', 'Transition pass enabled');
        } catch {
          Logger.warn('PostProcessing', 'Transition pass not available — skipping');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24. Anti-Aliasing — TRAA > SMAA > FXAA (mutually exclusive, must be LAST)
      //     `quality` (2|4|8|16|32) maps to ITERATED edge-resolve passes —
      //     log2(q) applications (2×=1 … 32×=5). This is the only real
      //     quality axis these post-AA algorithms expose (three's nodes have
      //     no internal presets, and WebGPU hardware MSAA caps at 4×): each
      //     extra pass genuinely resolves more residual edge aliasing at the
      //     cost of added softness + a full-screen pass. TRAA is temporal and
      //     must run exactly once — quality ≥ 8 chains ONE FXAA behind the
      //     resolve as the spatial cleanup step.
      // ════════════════════════════════════════════════════════════════════
      let aaApplied = false;
      // ── THE DISPLAY TRANSFORM BOUNDARY (2026-09-15) ────────────────────
      // Tone mapping + the output colour space used to run AFTER the whole
      // chain: three's RenderPipeline does it for `outputNode` when
      // `outputColorTransform` is true (the default), so the LUT, sharpen,
      // vignette, film grain, chromatic aberration and the colourblind
      // transform were all operating on unmapped linear HDR — values well
      // above 1 — even though each of their comments says "display-referred".
      // A LUT in particular is authored against display values; feeding it raw
      // radiance silently samples the wrong part of the cube.
      //
      // So the transform is applied HERE instead, once, at the boundary the
      // passes were written for: after the HDR chain and the anti-aliasing,
      // before the display-referred finishing stages. Everything upstream
      // stays in linear HDR, which is what bloom, DOF, god rays and the
      // atmosphere all assume.
      //
      // FXAA is the exception that proves the rule and moves across the line
      // WITH the transform: three documents it as needing tone-mapped,
      // output-encoded input, while SMAA's own header says the opposite
      // ("should be applied before converting colors to sRGB"). So SMAA stays
      // above the boundary and FXAA sits just below it.
      let displayTransformed = false;
      const toDisplay = (node: unknown): unknown => {
        if (displayTransformed) return node;
        displayTransformed = true;
        const out = renderOutput(node as never);
        // Registered HERE, not at the call site below, so the pass list's order
        // is the GRAPH's order even when FXAA pulls the boundary earlier.
        this.passes.set('outputTransform', { name: 'outputTransform', node: out, enabled: true });
        return out;
      };
      const aaIters = (q: number | undefined): number =>
        Math.max(1, Math.min(5, Math.round(Math.log2(Math.max(2, q ?? 2)))));
      /**
       * `aaQuality` (an FXAA-style 2/4/8 "x" number) → an SMAA PRESET.
       * SMAA quality is threshold + search length, not sample count, so the
       * mapping is by intent rather than arithmetic: x2 is the paper's medium
       * (three's built-in behaviour and the old effective default), x4 high,
       * x8 ultra. Anything below 2 takes the cheap preset.
       */
      const smaaPresetFor = (q: number | undefined): 'low' | 'medium' | 'high' | 'ultra' =>
        q === undefined ? 'medium' : q >= 8 ? 'ultra' : q >= 4 ? 'high' : q >= 2 ? 'medium' : 'low';

      // 24a. TRAA (highest quality — needs velocity buffer)
      const taau = this._upscale?.mode === 'taau';
      if (!aaApplied && (config?.traa?.enabled || taau)) {
        if (!scenePassVelocity) {
          Logger.warn('PostProcessing', 'TRAA requires velocity MRT — falling back to FXAA/SMAA');
        } else {
          try {
            const { traa } = await import('three/examples/jsm/tsl/display/TRAANode.js');
            if (this._disposed) return;
            const traaPass = traa(outputNode as any, scenePassDepth, scenePassVelocity, this.camera);
            outputNode = traaPass;
            this.passes.set('traa', { name: 'traa', node: traaPass, enabled: true });
            if (taau && this._upscale && config?.upscale) {
              // TAAU: sharpen the temporal resolve (RCAS at output resolution).
              const sharp = config.upscale.sharpness ?? FSR_PRESETS[config.upscale.quality].defaultSharpness;
              if (sharp > 0) {
                const rcas = createRCASNode({ srcColor: convertToTexture(outputNode as never) as never, srcWidth: this._upscale.w, srcHeight: this._upscale.h, sharpness: sharp });
                outputNode = rcas.node;
                this._upscale.rcas = rcas;
              }
              this.passes.set('upscale', { name: 'upscale', node: outputNode, enabled: true });
            }
            if ((config?.traa?.quality ?? 2) >= 8) {
              const { fxaa } = await import('three/examples/jsm/tsl/display/FXAANode.js');
              if (this._disposed) return;
              outputNode = fxaa(toDisplay(outputNode) as any); // FXAA wants display-referred input
            }
            aaApplied = true;
            Logger.info('PostProcessing', taau ? 'TAAU: temporal resolve over the low-resolution scene pass' : `TRAA anti-aliasing enabled (quality x${config?.traa?.quality ?? 2})`);
          } catch {
            Logger.warn('PostProcessing', 'TRAA pass not available');
          }
        }
      }

      // 24b. SMAA
      if (!aaApplied && config?.smaa?.enabled) {
        try {
          // The VENDORED node: three hard-codes the paper's "1x Medium"
          // preset with no way to reach the knobs. Quality here means the
          // real thing — edge threshold and pattern search length — not
          // running the filter more than once, which is what this used to do
          // (`aaIters`) and which is not what SMAA quality means: re-filtering
          // already-filtered edges does not search further along them.
          const { smaa, SMAA_PRESETS } = await import('./vendor/SMAAQualityNode.js');
          if (this._disposed) return;
          const preset = smaaPresetFor(config.smaa.quality);
          outputNode = smaa(outputNode as any, preset);
          this.passes.set('smaa', { name: 'smaa', node: outputNode, enabled: true });
          aaApplied = true;
          Logger.info('PostProcessing', `SMAA anti-aliasing enabled (${preset}: threshold ${SMAA_PRESETS[preset].threshold}, ${SMAA_PRESETS[preset].maxSearchSteps} search steps)`);
        } catch {
          Logger.warn('PostProcessing', 'SMAA pass not available');
        }
      }

      // 24c. FXAA (default)
      if (!aaApplied && config?.fxaa?.enabled !== false) {
        try {
          const { fxaa } = await import('three/examples/jsm/tsl/display/FXAANode.js');
          if (this._disposed) return;
          const n = aaIters(config?.fxaa?.quality);
          outputNode = toDisplay(outputNode); // FXAA wants display-referred input
          for (let i = 0; i < n; i++) outputNode = fxaa(outputNode as any);
          this.passes.set('fxaa', { name: 'fxaa', node: outputNode, enabled: true });
          if (n > 1) Logger.info('PostProcessing', `FXAA x${config?.fxaa?.quality ?? 2} → ${n} passes`);
        } catch {
          Logger.warn('PostProcessing', 'FXAA pass not available');
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24c1. Clouds (afterAA mode) — composited on the TEMPORALLY RESOLVED
      //       frame so TRAA's jitter never smears the cloud dither or makes
      //       the depth mask flicker at geometry silhouettes.
      // ════════════════════════════════════════════════════════════════════
      if (config?.clouds?.node && config.clouds.enabled !== false && config.clouds.afterAA) {
        try {
          outputNode = compositeClouds(outputNode);
          this.passes.set('clouds', { name: 'clouds', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Volumetric clouds pass enabled (post-AA composite)');
        } catch (err) {
          Logger.warn('PostProcessing', `Clouds pass (post-AA) failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24c1b. DISPLAY TRANSFORM — tone map + output colour space, ONCE, here.
      //        Everything above is linear HDR; everything below is
      //        display-referred. `outputColorTransform` is turned off at the
      //        end so three does not apply it a second time (see `toDisplay`).
      // ════════════════════════════════════════════════════════════════════
      outputNode = toDisplay(outputNode);
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24c2. Sharpen — post-AA (AA softens; the sharpen restores edge
      //       contrast), before distortion so the warp resamples the
      //       sharpened image.
      // ════════════════════════════════════════════════════════════════════
      if (config?.sharpen && config.sharpen.enabled !== false) {
        try {
          const sh = createSharpenNode(outputNode, config.sharpen);
          outputNode = sh.node;
          this._sharpenUniforms = sh.uniforms;
          this.passes.set('sharpen', { name: 'sharpen', node: sh.node, enabled: true });
          Logger.info('PostProcessing', 'Sharpen pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Sharpen failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24d. Lens distortion — AFTER AA (it warps by resampling; distorting
      //      first would alias the warped edges), then vignette on top.
      // ════════════════════════════════════════════════════════════════════
      if (config?.lensDistortion && config.lensDistortion.enabled !== false) {
        try {
          const ld = createLensDistortionNode(outputNode, config.lensDistortion);
          outputNode = ld.node;
          this._lensDistortionUniforms = ld.uniforms;
          this.passes.set('lensDistortion', { name: 'lensDistortion', node: ld.node, enabled: true });
          Logger.info('PostProcessing', 'Lens distortion pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Lens distortion failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 24e. Vignette — display-referred radial darkening.
      // ════════════════════════════════════════════════════════════════════
      if (config?.vignette && config.vignette.enabled !== false) {
        try {
          const vg = createVignetteNode(outputNode, config.vignette);
          outputNode = vg.node;
          this._vignetteUniforms = vg.uniforms;
          this.passes.set('vignette', { name: 'vignette', node: vg.node, enabled: true });
          Logger.info('PostProcessing', 'Vignette pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Vignette failed: ${err}`);
        }
      }
      if (this._disposed) return;

      // ════════════════════════════════════════════════════════════════════
      // 25. Accessibility colourblind correction — FINAL display-referred step.
      //     Applied after tone-map + AA so it operates on the same colours the
      //     user sees. No-op unless an explicit transform is supplied.
      // ════════════════════════════════════════════════════════════════════
      if (config?.colorblind?.transform && config.colorblind.enabled !== false) {
        try {
          outputNode = config.colorblind.transform(outputNode);
          this.passes.set('colorblind', { name: 'colorblind', node: outputNode, enabled: true });
          Logger.info('PostProcessing', 'Colourblind correction pass enabled');
        } catch (err) {
          Logger.warn('PostProcessing', `Colourblind pass failed: ${err}`);
        }
      }
      if (this._disposed) return;

      this.postProcessing.outputNode = outputNode as any;
      // The chain has already tone-mapped and encoded at the boundary above;
      // leaving three's own transform on would apply it a SECOND time.
      (this.postProcessing as unknown as { outputColorTransform?: boolean }).outputColorTransform = !displayTransformed;
      this._ready = true;

      Logger.info('PostProcessing', `Pipeline created with ${this.passes.size} passes`);
    } catch (err) {
      Logger.warn('PostProcessing', `Failed to create pipeline: ${err}`);
      this.postProcessing = null;
      this._ready = false;
    }
  }

  /**
   * Add a custom pass node.
   */
  addPass(name: string, node: unknown): void {
    this.passes.set(name, { name, node, enabled: true });
  }

  /**
   * Remove a pass by name.
   */
  removePass(name: string): boolean {
    return this.passes.delete(name);
  }

  /**
   * Enable or disable a pass.
   * Note: TSL-based post-processing chains are statically compiled.
   * This flag is tracked for external consumers (e.g. QualityManager)
   * but does NOT dynamically rebuild the pipeline. A full pipeline
   * rebuild is needed to actually add/remove passes from the chain.
   */
  setEnabled(name: string, enabled: boolean): void {
    const entry = this.passes.get(name);
    if (entry) entry.enabled = enabled;
  }

  /**
   * Check if a pass is enabled.
   */
  isEnabled(name: string): boolean {
    return this.passes.get(name)?.enabled ?? false;
  }

  /**
   * Get/set overall pipeline enabled state.
   */
  get enabled(): boolean {
    return this._enabled;
  }

  set enabled(value: boolean) {
    this._enabled = value;
  }

  /**
   * Update the camera reference (e.g., when switching cameras).
   */
  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
    // Also update the scene pass node's internal camera reference.
    // Without this, the post-processing pipeline continues rendering
    // with the old camera even though this.camera changed.
    if (this._scenePass) {
      this._scenePass.camera = camera;
    }
    // Refresh DOF near/far uniforms so the linearization tracks the new
    // camera. (Both PerspectiveCamera and OrthographicCamera expose `near`
    // and `far` as numeric properties.)
    const cam = camera as { near?: number; far?: number };
    if (this._dofNearUniform && typeof cam.near === 'number') {
      this._dofNearUniform.value = cam.near;
    }
    if (this._dofFarUniform && typeof cam.far === 'number') {
      this._dofFarUniform.value = cam.far;
    }
    // Refresh aerial-perspective camera uniforms (same rationale as DoF).
    if (this._aerialUniforms) {
      const p = camera as { near?: number; far?: number; fov?: number; aspect?: number };
      if (typeof p.near === 'number') this._aerialUniforms.near.value = p.near;
      if (typeof p.far === 'number') this._aerialUniforms.far.value = p.far;
      if (typeof p.fov === 'number') this._aerialUniforms.tanHalfFovY.value = Math.tan((p.fov * Math.PI) / 360);
      if (typeof p.aspect === 'number') this._aerialUniforms.aspect.value = p.aspect;
    }
    if (this._volumetricFogUniforms) {
      const p = camera as { near?: number; far?: number; fov?: number; aspect?: number };
      if (typeof p.near === 'number') this._volumetricFogUniforms.near.value = p.near;
      if (typeof p.far === 'number') this._volumetricFogUniforms.far.value = p.far;
      if (typeof p.fov === 'number') this._volumetricFogUniforms.tanHalfFovY.value = Math.tan((p.fov * Math.PI) / 360);
      if (typeof p.aspect === 'number') this._volumetricFogUniforms.aspect.value = p.aspect;
    }
    if (this._capsuleShadowsUniforms) {
      const p = camera as { near?: number; far?: number; fov?: number; aspect?: number };
      if (typeof p.near === 'number') this._capsuleShadowsUniforms.near.value = p.near;
      if (typeof p.far === 'number') this._capsuleShadowsUniforms.far.value = p.far;
      if (typeof p.fov === 'number') this._capsuleShadowsUniforms.tanHalfFovY.value = Math.tan((p.fov * Math.PI) / 360);
      if (typeof p.aspect === 'number') this._capsuleShadowsUniforms.aspect.value = p.aspect;
    }
  }

  /**
   * Render the post-processing pipeline.
   */
  render(): void {
    if (!this._enabled || !this._ready || !this.postProcessing) {
      // Fallback to raw render until pipeline is fully configured
      this.renderer.render(this.scene, this.camera);
      return;
    }

    // WHY refresh here: DOF depth linearization reads camera near/far. These
    // were only synced in setCamera(), so an in-place mutation of
    // camera.near/far (without re-calling setCamera) left the DOF uniforms
    // stale — contradicting the per-frame-refresh comment on the fields. This
    // is a couple of scalar writes, cheap enough to do every frame.
    const cam = this.camera as { near?: number; far?: number };
    if (this._dofNearUniform && typeof cam.near === 'number') {
      this._dofNearUniform.value = cam.near;
    }
    if (this._dofFarUniform && typeof cam.far === 'number') {
      this._dofFarUniform.value = cam.far;
    }
    this._refreshContactShadowUniforms();
    this._refreshUpscaleSizes();
    this._refreshCloudRtt();
    this._refreshLensFlareTexel();
    this._refreshKawase();

    this.postProcessing.render();
  }

  /** Keep the EASU/RCAS size uniforms in step with the drawing buffer (a resize
   *  or a live render-scale change); a couple of vec2 writes when unchanged. */
  private _refreshUpscaleSizes(): void {
    const u = this._upscale;
    if (!u) return;
    const size = this.renderer.getDrawingBufferSize(_upscaleSize);
    const w = Math.max(1, Math.round(size.x)), h = Math.max(1, Math.round(size.y));
    if (w === u.w && h === u.h) return;
    u.w = w; u.h = h;
    const sw = Math.max(1, Math.round(w / u.scale)), sh = Math.max(1, Math.round(h / u.scale));
    if (u.easu) {
      (u.easu.uniforms.srcSize as { value: THREE.Vector2 }).value.set(sw, sh);
      (u.easu.uniforms.dstSize as { value: THREE.Vector2 }).value.set(w, h);
    }
    if (u.rcas) (u.rcas.uniforms.invSrcSize as { value: THREE.Vector2 }).value.set(1 / w, 1 / h);
  }

  /** The drawing buffer size, tolerant of renderers without the query (a 1080p fallback). */
  private _drawingBufferSize(): { x: number; y: number } {
    const r = this.renderer as { getDrawingBufferSize?: (t: THREE.Vector2) => THREE.Vector2 };
    return typeof r.getDrawingBufferSize === 'function' ? r.getDrawingBufferSize(_upscaleSize) : { x: 1920, y: 1080 };
  }

  /** Keep the lens-flare texel size in step with the drawing buffer. */
  /** Dual-Kawase bloom (stage 4): the level texel sizes + RTT sizes follow the drawing buffer. */
  private _kawaseSize = { w: 0, h: 0 };
  private _refreshKawase(): void {
    if (!this._kawase) return;
    const size = this._drawingBufferSize();
    if (size.x === this._kawaseSize.w && size.y === this._kawaseSize.h) return;
    this._kawaseSize = { w: size.x, h: size.y };
    try { this._kawase.setSize(size.x, size.y); } catch { /* best-effort */ }
  }

  private _refreshLensFlareTexel(): void {
    const lf = this._lensFlare as { uniforms?: { texelSize?: { value?: { x: number; y: number; set?: (x: number, y: number) => void } } } } | null;
    const t = lf?.uniforms?.texelSize?.value;
    if (!t) return; // three's LensflareNode sizes itself
    const size = this._drawingBufferSize();
    const tx = 1 / Math.max(1, size.x), ty = 1 / Math.max(1, size.y);
    if (Math.abs(t.x - tx) > 1e-9 || Math.abs(t.y - ty) > 1e-9) { if (t.set) t.set(tx, ty); else { t.x = tx; t.y = ty; } }
  }

  /**
   * Release the fractional-resolution cloud RTT. WHY: `rtt()` is an RTTNode
   * owning a RenderTarget (rs × the drawing buffer) and has no dispose() of
   * its own; it lives only in `_cloudRtt`, outside `passes`, so every tier
   * rebuild / world transition leaked one RGBA target (2026-09-12).
   */
  private _disposeCloudRtt(): void {
    const c = this._cloudRtt;
    if (!c) return;
    this._cloudRtt = null;
    const rt = (c.node as { renderTarget?: { dispose?: () => void } }).renderTarget;
    try { rt?.dispose?.(); } catch (err) { Logger.warn('PostProcessing', `cloud RTT dispose failed: ${err}`); }
  }

  /** Keep the cloud RTT at rs × the drawing buffer across resizes. */
  private _refreshCloudRtt(): void {
    const c = this._cloudRtt;
    if (!c) return;
    const size = this.renderer.getDrawingBufferSize(_upscaleSize);
    if (size.x === c.w && size.y === c.h) return;
    c.w = size.x; c.h = size.y;
    c.node.setSize?.(Math.max(1, Math.round(size.x * c.rs)), Math.max(1, Math.round(size.y * c.rs)));
  }

  /** The current upscale ratio (output / render), 1 when no upscale is built. */
  get renderScale(): number { return this._upscale?.scale ?? 1; }

  /**
   * Move the render scale LIVE (a frame-time governor's lever): the scene pass
   * re-targets to output/scale on its next render and the EASU kernel follows.
   * No pipeline rebuild. Only when an upscale is built; clamped to [1, 2.5].
   */
  setRenderScale(scale: number): boolean {
    const u = this._upscale;
    if (!u || !this._scenePass) return false;
    const s2 = Math.max(1, Math.min(2.5, scale));
    if (Math.abs(s2 - u.scale) < 1e-4) return true;
    u.scale = s2;
    (this._scenePass as { setResolutionScale?: (s: number) => void }).setResolutionScale?.(1 / s2);
    u.w = -1; // force the size refresh
    this._refreshUpscaleSizes();
    return true;
  }

  /**
   * List pass names.
   */
  getPassNames(): string[] {
    return Array.from(this.passes.keys());
  }

  dispose(): void {
    this._disposed = true;
    this._ready = false;
    // WHY: three r185 RenderPipeline.dispose() frees only its fullscreen-quad
    // material. The scene PassNode's (MRT) render target and every display
    // node's internal targets — bloom mip chain, GTAO/SSGI/SSR/denoise, TRAA
    // history, DoF, blur, transition's second scene pass — are released only
    // by their OWN dispose(), which nothing called: every pipeline teardown /
    // quality-tier rebuild leaked a full-resolution HDR target set. A plain
    // TSL Node.dispose() just dispatches its 'dispose' event (no renderer
    // listeners), so calling it on every registered pass is safe.
    for (const entry of this.passes.values()) {
      this._disposeNode(entry.node, entry.name);
    }
    for (const owned of this._ownedPassNodes) this._disposeNode(owned, 'pass');
    this._ownedPassNodes.length = 0;
    this._disposeCloudRtt();
    this._disposeNode(this._scenePass, 'scene');
    this.postProcessing?.dispose();
    this.postProcessing = null;
    this.passes.clear();
    this._godRays = null;
    this._lensFlare = null;
    this._contactShadowsUniforms = null;
    this._scenePass = null;
  }

  /** Best-effort node teardown — a throwing dispose must not abort the rest. */
  private _disposeNode(node: unknown, label: string): void {
    const d = node as { dispose?: () => void } | null | undefined;
    if (!d || typeof d.dispose !== 'function') return;
    try {
      d.dispose();
    } catch (err) {
      Logger.debug('PostProcessing', `Pass "${label}" dispose failed: ${err}`);
    }
  }
}
